import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

/**
 * Reject one or several procurement requests with a MANDATORY reason.
 *
 * Props:
 *   isOpen, toggle, request (single, optional), bulkCount (number, optional),
 *   onReject(reason), isSubmitting
 *
 * When bulkCount > 0 the modal is in bulk mode (one shared reason for all selected).
 */
const RejectProcurementRequestModal = ({
  isOpen,
  toggle,
  request,
  bulkCount = 0,
  onReject,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  const isBulk = bulkCount > 0;

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError(t('planning_purchasing.reason_required', 'A reason is required'));
      return;
    }
    onReject(reason.trim());
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header">
          <h5 className="modal-title">{t('planning_purchasing.reject_request', 'Reject Request')}</h5>
          <button type="button" className="btn-close" onClick={toggle} aria-label={t('shared.close', 'Close')} />
        </div>
        <div className="modal-body">
          <p className="mb-3">
            {isBulk ? (
              <>
                {t('planning_purchasing.confirm_bulk_reject', 'Are you sure you want to reject')}{' '}
                <strong>{bulkCount}</strong> {t('planning_purchasing.requests_lower', 'requests')}?
              </>
            ) : (
              <>
                {t('planning_purchasing.confirm_reject', 'Are you sure you want to reject request')}{' '}
                <strong>{request?.requestNumber}</strong>?
              </>
            )}
          </p>
          {!isBulk && request?.title && (
            <p className="text-muted small mb-3">
              <strong>{t('shared.title', 'Title')}:</strong> {request.title}
            </p>
          )}
          <div className="form-group">
            <label htmlFor="rejectReason" className="form-label fw-medium">
              {t('planning_purchasing.rejection_reason', 'Rejection Reason')} <span className="text-danger">*</span>
            </label>
            <textarea
              id="rejectReason"
              className={`form-control ${error ? 'is-invalid' : ''}`}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError('');
              }}
              rows={3}
              placeholder={t('planning_purchasing.reason_placeholder', 'Provide a reason for rejecting...')}
              disabled={isSubmitting}
            />
            {error && <small className="text-danger">{error}</small>}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={toggle} disabled={isSubmitting}>
            {t('shared.cancel', 'Cancel')}
          </button>
          <button type="button" className="btn btn-danger" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <><Spinner size="sm" className="me-2" />{t('planning_purchasing.rejecting', 'Rejecting...')}</>
            ) : (
              t('planning_purchasing.reject_button', 'Reject')
            )}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default RejectProcurementRequestModal;
